import Cache from '@aeppic/shared/cache.js'

type RgbColor = {
    r: number;
    g: number;
    b: number;
}

const COLOR_CACHE: Cache<RgbColor> = new Cache<RgbColor>(100, 60 * 60) // 1 hour cache

/**
 * Parses a CSS color string to RGB values using DOM.
 * Assumes browser environment.
 */
function parseColorToRGB(color: string): RgbColor {
    return COLOR_CACHE.lookupSync(color, () => {
        const el = document.createElement('div')
        el.style.color = color
        document.body.appendChild(el)
        const rgbString = window.getComputedStyle(el).color
        document.body.removeChild(el)
        const match = rgbString.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/)
        if (!match) {
            throw new Error('Invalid color format')
        }
        
        const colorValue: RgbColor = {
            r: parseInt(match[1], 10),
            g: parseInt(match[2], 10),
            b: parseInt(match[3], 10),
        }

        return colorValue
    })
}

/**
 * Calculates relative luminance of a color.
 * Uses WCAG formula.
 */
function getLuminance(color: string): number {
  const { r, g, b } = parseColorToRGB(color)
  const rs = r / 255
  const gs = g / 255
  const bs = b / 255
  const R = rs <= 0.04045 ? rs / 12.92 : Math.pow((rs + 0.055) / 1.055, 2.4)
  const G = gs <= 0.04045 ? gs / 12.92 : Math.pow((gs + 0.055) / 1.055, 2.4)
  const B = bs <= 0.04045 ? bs / 12.92 : Math.pow((bs + 0.055) / 1.055, 2.4)
  return 0.2126 * R + 0.7152 * G + 0.0722 * B
}

/**
 * Determines if the color is dark (true) or light (false).
 * Threshold: 0.5 for simplicity; adjust if needed.
 */
export function isDark(color: string, threshold = 0.5): boolean {
  return getLuminance(color) <= threshold
}

export const Colors = {
    isDark
}